import { useEffect, useState } from "react";
import { CheckCircle, Warning, Shield } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { setToken } from "../lib/api";

export default function AccountSettings() {
  const [me, setMe] = useState(null);
  const [emailForm, setEmailForm] = useState({ current_password: "", new_email: "" });
  const [pwdForm, setPwdForm] = useState({
    current_password: "",
    new_password: "",
    confirm_new_password: "",
  });
  const [busy, setBusy] = useState(null);
  const [pwdError, setPwdError] = useState(null);
  const [emailError, setEmailError] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/account");
      setMe(data);
    } catch (e) {
      toast.error("Failed to load account");
    }
  };
  useEffect(() => { refresh(); }, []);

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailError(null);
    setBusy("email");
    try {
      const { data } = await api.post("/admin/account/change-email", emailForm);
      if (data.token) setToken(data.token);
      toast.success("Email updated.");
      setEmailForm({ current_password: "", new_email: "" });
      refresh();
    } catch (e) {
      setEmailError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  const submitPwd = async (e) => {
    e.preventDefault();
    setPwdError(null);
    if (pwdForm.new_password !== pwdForm.confirm_new_password) {
      setPwdError("New password and confirmation do not match.");
      return;
    }
    if (pwdForm.new_password.length < 12) {
      setPwdError("Password must be at least 12 characters.");
      return;
    }
    setBusy("pwd");
    try {
      const { data } = await api.post("/admin/account/change-password", pwdForm);
      if (data.token) setToken(data.token);
      toast.success("Password changed. Existing sessions invalidated.");
      setPwdForm({ current_password: "", new_password: "", confirm_new_password: "" });
      refresh();
    } catch (e) {
      setPwdError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  if (!me) return <div className="text-white/60">Loading account…</div>;

  const isBootstrap = me.bootstrapped_from_env && !me.password_changed_at;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-account-settings">
      {isBootstrap && (
        <div
          className="rounded-md border border-amber-400/40 bg-amber-500/10 text-amber-200 px-4 py-3 text-sm flex items-start gap-2"
          data-testid="bootstrap-warning"
        >
          <Warning size={18} className="mt-0.5" />
          <div>
            <strong>This account is using the bootstrap credentials from environment variables.</strong>
            <br />
            For production safety, please rotate the password (and optionally the email) below.
          </div>
        </div>
      )}

      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-5">
        <div className="overline text-[var(--cc-gold)] mb-3 inline-flex items-center gap-2"><Shield size={14} /> Account</div>
        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-white/50">Email</dt><dd className="font-mono-num" data-testid="account-current-email">{me.email}</dd>
          <dt className="text-white/50">Role</dt><dd className="font-mono-num">{me.role}</dd>
          <dt className="text-white/50">Created</dt><dd className="text-white/70">{me.created_at ? new Date(me.created_at).toLocaleString("en-GB") : "—"}</dd>
          <dt className="text-white/50">Last password change</dt><dd className="text-white/70" data-testid="account-pwd-changed-at">{me.password_changed_at ? new Date(me.password_changed_at).toLocaleString("en-GB") : "never"}</dd>
        </dl>
      </div>

      <form onSubmit={submitPwd} className="rounded-lg border border-white/10 bg-white/[0.02] p-5 space-y-3" data-testid="change-password-form">
        <div className="overline text-[var(--cc-gold)] mb-2">Change password</div>
        <Input label="Current password" type="password" value={pwdForm.current_password} onChange={(v) => setPwdForm({ ...pwdForm, current_password: v })} testId="pwd-current" required />
        <Input label="New password (min 12 chars)" type="password" value={pwdForm.new_password} onChange={(v) => setPwdForm({ ...pwdForm, new_password: v })} testId="pwd-new" minLength={12} required />
        <Input label="Confirm new password" type="password" value={pwdForm.confirm_new_password} onChange={(v) => setPwdForm({ ...pwdForm, confirm_new_password: v })} testId="pwd-confirm" minLength={12} required />
        {pwdError && (
          <div className="text-red-300 text-xs inline-flex items-center gap-1" data-testid="pwd-error">
            <Warning size={12} /> {pwdError}
          </div>
        )}
        <button type="submit" disabled={busy === "pwd"} data-testid="pwd-submit" className="btn-gold px-4 py-2 rounded text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
          <CheckCircle size={14} weight="bold" /> {busy === "pwd" ? "Changing…" : "Change password"}
        </button>
      </form>

      <form onSubmit={submitEmail} className="rounded-lg border border-white/10 bg-white/[0.02] p-5 space-y-3" data-testid="change-email-form">
        <div className="overline text-[var(--cc-gold)] mb-2">Change email</div>
        <Input label="Current password" type="password" value={emailForm.current_password} onChange={(v) => setEmailForm({ ...emailForm, current_password: v })} testId="email-current-pwd" required />
        <Input label="New email" type="email" value={emailForm.new_email} onChange={(v) => setEmailForm({ ...emailForm, new_email: v })} testId="email-new" required />
        {emailError && (
          <div className="text-red-300 text-xs inline-flex items-center gap-1" data-testid="email-error">
            <Warning size={12} /> {emailError}
          </div>
        )}
        <button type="submit" disabled={busy === "email"} data-testid="email-submit" className="btn-gold px-4 py-2 rounded text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
          <CheckCircle size={14} weight="bold" /> {busy === "email" ? "Updating…" : "Change email"}
        </button>
      </form>
    </div>
  );
}

const Input = ({ label, value, onChange, type = "text", testId, required, minLength }) => (
  <label className="block">
    <div className="overline text-white/50 mb-1.5">{label}</div>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      required={required}
      minLength={minLength}
      className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm outline-none focus:border-[var(--cc-gold)]"
    />
  </label>
);
